import {
  DuplicateJournalResultEntity,
  JournalCountsEntity,
  JournalEntity,
  JournalsResultEntity,
  PaginationInfo,
} from "../../domain/entities/JournalEntity";
import {
  DuplicateJournalResponseDTO,
  JournalCountsResponseDTO,
  JournalDTO,
  JournalsResponseDTO,
  PaginationDTO,
} from "../models/JournalDTOs";

const toBooleanFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1";

const toNumber = (value: unknown): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export class JournalMapper {
  static toJournalEntity(dto: JournalDTO): JournalEntity {
    return {
      id: toNumber(dto.id),
      zoho_journalid: dto.zoho_journalid ?? null,
      tally_remote_id: dto.tally_remote_id ?? null,
      tally_voucher_number: dto.tally_voucher_number ?? null,
      tally_master_id: toNullableNumber(dto.tally_master_id),
      posted_to_tally_date: dto.posted_to_tally_date ?? null,
      // ZOUR-65 F4: this list is explicit, so a field missing from it never
      // reaches the screen no matter what the API sends — the bug class that
      // cost phase 2 the most time.
      location_id: dto.location_id ?? null,
      date: dto.date,
      journal_number: dto.journal_number || dto.reference_number || `${dto.id}`,
      reference_number: dto.reference_number ?? null,
      notes: dto.notes ?? "",
      status: dto.status,
      type: dto.type,
      currency_id: dto.currency_id,
      currency_symbol: dto.currency_symbol ?? "",
      template_id: toNullableNumber(dto.template_id),
      is_generated_from_template: toBooleanFlag(dto.is_generated_from_template),
      total_debit: toNumber(dto.total_debit),
      total_credit: toNumber(dto.total_credit),
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      approved_at: dto.approved_at,
      recurrence_frequency: dto.recurrence_frequency,
      recurrence_interval: toNullableNumber(dto.recurrence_interval),
      journal_type: dto.journal_type,
    };
  }

  static toPaginationEntity(dto: PaginationDTO): PaginationInfo {
    return {
      total: toNumber(dto.total),
      limit: toNumber(dto.limit),
      offset: toNumber(dto.offset),
      has_more: toBooleanFlag(dto.has_more),
      current_page: dto.current_page,
      total_pages: dto.total_pages,
    };
  }

  static toJournalsResultEntity(dto: JournalsResponseDTO): JournalsResultEntity {
    return {
      journals: (dto.data?.journals ?? []).map((journal) =>
        this.toJournalEntity(journal),
      ),
      pagination: this.toPaginationEntity(
        dto.data?.pagination ?? {
          total: 0,
          limit: 0,
          offset: 0,
          has_more: false,
        },
      ),
    };
  }

  static toJournalCountsEntity(dto: JournalCountsResponseDTO): JournalCountsEntity {
    const journals = dto.data?.journals;
    const recurringTemplates = dto.data?.recurring_templates;
    const cancelled = toNumber(recurringTemplates?.cancelled);
    const expired = toNumber(recurringTemplates?.expired);

    return {
      journals: {
        total: toNumber(journals?.total),
        underReview: toNumber(journals?.under_review),
        saveAsDraft: toNumber(journals?.save_as_draft),
        rejected: toNumber(journals?.rejected),
        approved: toNumber(journals?.approved),
        recurring: toNumber(journals?.recurring),
      },
      recurringTemplates: {
        total: toNumber(recurringTemplates?.total),
        active: toNumber(recurringTemplates?.active),
        paused: toNumber(recurringTemplates?.paused),
        cancelled,
        expired,
        ended: cancelled + expired,
      },
    };
  }

  static toDuplicateJournalResultEntity(
    dto: DuplicateJournalResponseDTO,
  ): DuplicateJournalResultEntity {
    return {
      success: dto.success,
      message: dto.message,
      newJournalId: dto.data?.new_journal_id,
      originalJournalId: dto.data?.original_journal_id,
    };
  }
}
